import React, { useEffect, useState } from 'react';
import { CONVERSATION_SHORTCUT_LABEL } from '../../lib/conversationBridge';
import { recentVisits } from '../../lib/navMemory';
import { useLocation, useNavigate } from 'react-router';
import {
  Building2,
  LayoutDashboard,
  Briefcase,
  BookOpen,
  Bot,
  DollarSign,
  HelpCircle,
  Home,
  Grid3x3,
  ChevronRight,
  ChevronDown,
  ChevronLeft,
  MoreHorizontal,
  CreditCard,
  BarChart2,
  DoorOpen,
  ClipboardCheck,
  Users,
  GraduationCap,
  UserCog,
  BookMarked,
  ClipboardList,
  HeartHandshake,
  MessageCircle,
  UserPlus,
  ShieldCheck,
  Sparkles,
  Timer,
  Bell,
  FileText,
  Filter,
  Gauge,
  Wallet,
  Plug,
  CalendarDays,
  Package,
  ShoppingCart,
  MessageSquare,
  PlayCircle,
  Trello,
  Headphones,
  Receipt,
  TrendingUp,
  Landmark,
  Tag,
} from 'lucide-react';
import { Badge } from '../ui/badge';
import { UserAvatar } from '../UserAvatar';
import logoIcone from '@/assets/img/logo-icone.png?url';
import logoText from '@/assets/img/logo-text-transparent.png?url';
import { useAuth } from '../../hooks/useAuth';
import { whatsappService, type WhatsAppStatus } from '../../services/whatsapp.service';
import { usePendingActionsCount } from '../../hooks/usePendingActionsCount';
import { useSolicitationsInboxCount } from '../../hooks/useSolicitationsInboxCount';
import { useAttendanceUnreadCount } from '../../hooks/useAttendanceUnreadCount';
import { usePermissions } from '../../contexts/PermissionContext';
import HealthStatusWidget from './HealthStatusWidget';
import { ThemeToggle } from './ThemeToggle';
import { DARK_THEME_ENABLED } from '../../contexts/ThemeContext';

interface NavigationSidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

interface NavItem {
  name: string;
  icon: React.ReactNode;
  path: string;
  badge?: number;
  badgeClassName?: string;
  disabled?: boolean;
  rolesRequired?: string[];
  // Slug de AppModule (ver permissionService.ts::CONFIGURABLE_APP_MODULES).
  // Gestor pode restringir esta tela por setor em Escola → Setores →
  // "Telas visíveis". Ausente = sempre visível a quem passar em rolesRequired.
  moduleRequired?: string;
  // Roles que veem este item mesmo sem `moduleRequired` habilitado no setor —
  // usado quando o módulo de UI não tem granularidade por rota (ex:
  // 'secretaria' gate tanto o inbox quanto Documentos/Matrículas) mas a tela
  // em questão já é protegida corretamente por outro mecanismo (ver
  // PermissionGuard.tsx e o item "Atendimentos").
  moduleBypassRoles?: string[];
  // Destino ao clicar no item com a sidebar recolhida (sem submenu visível).
  // Ausente = usa `path` normalmente (comportamento antigo).
  collapsedPath?: string;
  children?: NavItem[];
}

const mainNavItems: NavItem[] = [
  { name: 'Início', icon: <Home className="size-5" />, path: '/inicio', badgeClassName: 'bg-red-500', rolesRequired: ['manager', 'coordinator'] },
  // Tela de entrada do operador: o que é dele em todos os boards (my-desk).
  { name: 'Minha mesa', icon: <ClipboardCheck className="size-5" />, path: '/minha-mesa', rolesRequired: ['operator'] },
  {
    // Link único pro Painel de Atendimento — a antiga "Visão Geral" (painel
    // da fila) foi absorvida pelo Início.
    name: 'Atendimentos',
    icon: <Building2 className="size-5" />,
    path: '/atendimento/conversas',
    badgeClassName: 'bg-indigo-600',
    moduleRequired: 'secretaria',
    // Operador sem 'secretaria' habilitado no setor ainda precisa alcançar o
    // Painel de Atendimento (inbox já escopado por setor no backend).
    moduleBypassRoles: ['operator'],
  },
  {
    // Módulo próprio ('captacao'), separado de 'secretaria' — antes os dois
    // grupos dividiam o mesmo slug e o gestor não conseguia liberar um sem
    // o outro (ver apps/schools/constants.py, eleveia-api).
    // Link único (abre o Board): Visão Geral, Follow-up e Configurar são abas
    // no topo das telas — ver CaptacaoTabs.tsx.
    name: 'Captação',
    icon: <UserPlus className="size-5" />,
    path: '/atendimento/captacao',
    moduleRequired: 'captacao',
  },
  {
    // Link único (abre o Board), abas no topo como na Captação — ver
    // MatriculaTabs.tsx. Mesmo módulo da rota (/atendimento/matriculas).
    name: 'Matrícula',
    icon: <GraduationCap className="size-5" />,
    path: '/atendimento/matriculas',
    moduleRequired: 'secretaria',
  },
  {
    // Tela básica: Kanban/boards nunca é restringível por setor (ver
    // ALWAYS_VISIBLE_MODULES em apps/schools/constants.py, eleveia-api) —
    // "Setores" (admin, manager/coordinator, em /boards) e "Setores" do operador (/workspace, abaixo)
    // são a mesma feature por dois pontos de entrada, nenhum dos dois deve
    // sumir por configuração de setor.
    // Acompanhamento de todos os pedidos das famílias (substitui a Triagem):
    // uma linha por pedido, com o estado de cada setor envolvido.
    name: 'Solicitações',
    icon: <ClipboardList className="size-5" />,
    path: '/solicitacoes',
    badgeClassName: 'bg-red-500',
    rolesRequired: ['manager', 'coordinator'],
  },
  {
    // Link único: a antiga "Visão Geral" virou os chips de setor dessa tela.
    name: 'Setores',
    icon: <Grid3x3 className="size-5" />,
    path: '/boards',
    rolesRequired: ['manager', 'coordinator'],
  },
  {
    // Tela básica pra todo operador — ver comentário acima em "Setores".
    // Mesmo nome da gestão: é o mesmo lugar (só mostra os boards do setor de
    // quem está logado) — "Workspace" e "Setores" confundiam quando um
    // explicava para o outro.
    name: 'Setores',
    icon: <Grid3x3 className="size-5" />,
    path: '/workspace',
    rolesRequired: ['operator'],
  },
  {
    name: 'Agente I.A',
    icon: <Bot className="size-5" />,
    path: '/agent-config',
    moduleRequired: 'agente_ia',
    children: [
      { name: 'Personalidade', icon: <Sparkles className="size-4" />, path: '/agent-config?section=personality' },
      { name: 'Horário de Atendimento', icon: <Timer className="size-4" />, path: '/agent-config?section=horario' },
      { name: 'SDR', icon: <GraduationCap className="size-4" />, path: '/agent-config?section=matricula' },
      { name: 'Perfil da Escola', icon: <Building2 className="size-4" />, path: '/agent-config?section=perfil-escola' },
      { name: 'Inatividade', icon: <Bell className="size-4" />, path: '/agent-config?section=inatividade' },
      { name: 'Encerramento & NPS', icon: <ClipboardCheck className="size-4" />, path: '/agent-config?section=nps' },
      { name: 'Base de FAQ', icon: <HelpCircle className="size-4" />, path: '/agent-config?section=faq' },
      { name: 'Base de Conhecimento', icon: <BookOpen className="size-4" />, path: '/agent-config?section=knowledge' },
      { name: 'Documentos para Envio', icon: <FileText className="size-4" />, path: '/agent-config?section=parent-docs' },
      { name: 'Roteamento', icon: <Filter className="size-4" />, path: '/agent-config?section=triagem' },
    ],
  },
  { name: 'Direção', icon: <Briefcase className="size-5" />, path: '#', disabled: true },
  {
    name: 'Pedagógico',
    icon: <BookOpen className="size-5" />,
    path: '/pedagogico',
    moduleRequired: 'pedagogico',
    children: [
      { name: 'Visão Geral', icon: <LayoutDashboard className="size-4" />, path: '/pedagogico' },
      { name: 'Turmas', icon: <Users className="size-4" />, path: '/pedagogico/turmas' },
      { name: 'Alunos', icon: <GraduationCap className="size-4" />, path: '/pedagogico/alunos' },
      { name: 'Professores', icon: <UserCog className="size-4" />, path: '/pedagogico/professores' },
      { name: 'Matérias', icon: <BookMarked className="size-4" />, path: '/pedagogico/materias' },
      { name: 'Notas & Diário', icon: <ClipboardList className="size-4" />, path: '/pedagogico/notas' },
      { name: 'Responsáveis', icon: <HeartHandshake className="size-4" />, path: '/pedagogico/responsaveis' },
    ],
  },
  { name: 'Financeiro', icon: <DollarSign className="size-5" />, path: '#', disabled: true },
];

const toolsNavItems: NavItem[] = [
  { name: 'Calendário', icon: <CalendarDays className="size-5" />, path: '/calendario', moduleRequired: 'calendario' },
  // 'estoque' e 'loja' (rotulado "Venda" na config) são módulos separados —
  // antes os dois dividiam o mesmo slug ('loja') e não dava pra liberar um
  // sem o outro (ver apps/schools/constants.py, eleveia-api).
  { name: 'Estoque', icon: <Package className="size-5" />, path: '/loja/estoque', rolesRequired: ['manager'], moduleRequired: 'estoque' },
  { name: 'Venda', icon: <ShoppingCart className="size-5" />, path: '/loja/venda', moduleRequired: 'loja' },
  // Tela básica pra todo operador — chat interno nunca é restringível por
  // setor (ver ALWAYS_VISIBLE_MODULES em apps/schools/constants.py, eleveia-api).
  { name: 'Chat da Equipe', icon: <MessageCircle className="size-5" />, path: '/chat' },
  { name: 'Integrações', icon: <Plug className="size-5" />, path: '/integracoes', rolesRequired: ['manager'], moduleRequired: 'escola' },
  // { name: 'Relatórios', icon: <BarChart2 className="size-5" />, path: '/relatorios', rolesRequired: ['manager'] }, #Fut
  // { name: 'Meu Plano', icon: <CreditCard className="size-5" />, path: '/billing', rolesRequired: ['manager'] },
  { name: 'Tutoriais', icon: <PlayCircle className="size-5" />, path: '/tutoriais' },
  { name: 'Suporte',   icon: <HelpCircle className="size-5" />, path: '/suporte' },
];

const NAV_PATHS = [...mainNavItems, ...toolsNavItems]
  .flatMap((i) => [i.path, ...(i.children?.map((c) => c.path) ?? [])])
  .filter((p) => p !== '#' && !p.includes('?'));

/** A página pertence à área do item? (o item mais específico ganha:
 *  /atendimento/captacao/follow-up é de "Follow-up", não de "Board"). */
function ownsPath(itemPath: string, pathname: string): boolean {
  // Boards de setor (/board/:sector) são abertos a partir de Setores/Workspace.
  if (pathname.startsWith('/board/')) return itemPath === '/boards' || itemPath === '/workspace';
  const inArea = (p: string) => pathname === p || pathname.startsWith(`${p}/`);
  if (!inArea(itemPath)) return false;
  return !NAV_PATHS.some((p) => p.length > itemPath.length && inArea(p));
}

export default function NavigationSidebar({ collapsed, onToggle, mobileOpen, onMobileClose }: NavigationSidebarProps) {
  // Largura ocupada pela barra (0 no celular, onde ela vira gaveta) — o card
  // aberto usa para cobrir só a área principal e deixar a barra clicável.
  useEffect(() => {
    const desktop = window.matchMedia('(min-width: 768px)');
    const apply = () => document.documentElement.style.setProperty(
      '--app-sidebar-w', desktop.matches ? (collapsed ? '72px' : '240px') : '0px',
    );
    apply();
    desktop.addEventListener('change', apply);
    return () => desktop.removeEventListener('change', apply);
  }, [collapsed]);

  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { permissions, loading: permissionsLoading, error: permissionsError } = usePermissions();

  useEffect(() => {
    onMobileClose?.();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const [waStatus, setWaStatus] = useState<WhatsAppStatus>('disconnected');

  const userRole = user?.profile?.role ?? '';
  // Enquanto /auth/me/permissions/ ainda não respondeu (ou falhou), não
  // esconde nada por módulo — só o rolesRequired (já síncrono via useAuth)
  // filtra nesse meio tempo. Falha ao buscar permissões nunca deve travar
  // a navegação por completo; is_staff não depende disso (item set próprio).
  const hasModule = (slug?: string): boolean => {
    if (!slug) return true;
    if (permissionsLoading || permissionsError) return true;
    return permissions?.allowed_modules.includes(slug) ?? true;
  };
  // Contas is_staff (equipe interna Eleve.IA) recebem role='manager' por padrão no
  // backend (signal create_superuser_profile), mesmo sem escola vinculada
  // (profile.school=null) — por isso NÃO dá pra usar userRole pra decidir o que
  // mostrar pro admin. is_staff é o sinal correto: área interna, nada escolar.
  const isStaff = !!user?.is_staff;
  const pendingActionsCount = usePendingActionsCount();
  const solicitationsInboxCount = useSolicitationsInboxCount();
  const attendanceUnreadCount = useAttendanceUnreadCount();

  // Busca status do WhatsApp para o dot indicador — só faz sentido pra conta de
  // escola; is_staff não tem escola vinculada e o School Selector nem renderiza.
  useEffect(() => {
    if (isStaff) return;
    let cancelled = false;
    whatsappService
      .getWhatsAppStatus()
      .then((res) => {
        if (!cancelled) setWaStatus(res.status);
      })
      .catch(() => {
        if (!cancelled) setWaStatus('disconnected');
      });
    return () => {
      cancelled = true;
    };
  }, []);


  const fullName = user
    ? (`${user.first_name} ${user.last_name}`.trim() || user.username)
    : '—';
  const roleDisplay = user?.profile?.role_display ?? '';
  const schoolName = user?.profile?.school_name ?? '';

  const isChildActive = (child: NavItem) => {
    // '/boards' é o path de "Todos os Boards" e também prefixo de
    // '/boards/visao-geral' e '/boards/triagem' — match exato evita
    // destacar "Todos os Boards" junto com os outros itens do grupo.
    if (child.path === '/boards') return location.pathname === '/boards';
    const [childPathname, childQuery] = child.path.split('?');
    if (childQuery) {
      if (location.pathname !== childPathname) return false;
      const childParams = new URLSearchParams(childQuery);
      const currentParams = new URLSearchParams(location.search);
      return Array.from(childParams.entries()).every(([key, value]) => {
        const current = currentParams.get(key);
        // sem '?section=' na URL, AgentConfig.tsx assume 'personality' como padrão
        if (current === null && key === 'section' && value === 'personality') return true;
        // sem '?tab=' na URL, FinanceInterno.tsx assume 'dashboard' como padrão
        if (current === null && key === 'tab' && value === 'dashboard') return true;
        return current === value;
      });
    }
    return location.pathname === child.path || location.pathname.startsWith(`${child.path}/`);
  };

  const isActive = (item: NavItem) => {
    if (item.disabled) return false;
    if (item.children) return item.children.some(isChildActive);
    if (item.path === '/') return location.pathname === '/';
    return location.pathname.startsWith(item.path);
  };

  // Volta para onde a pessoa estava na área (o board, a aba, o filtro na URL;
  // o card deixado aberto o AppLayout reabre). Já dentro da área, o clique
  // leva à raiz dela, como sempre.
  const handleNav = (item: NavItem) => {
    if (item.disabled || item.path === '#') return;
    const target = collapsed && item.collapsedPath ? item.collapsedPath : item.path;
    // window.location, não useLocation: enquanto a página nova (lazy) carrega,
    // o router ainda mostra — e informa — a anterior.
    if (!target.includes('?') && !ownsPath(target, window.location.pathname)) {
      const last = recentVisits().find((url) => ownsPath(target, url.split('?')[0]));
      if (last) {
        navigate(last);
        return;
      }
    }
    navigate(target);
  };

  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    mainNavItems.forEach((item) => {
      if (item.children?.some(isChildActive)) initial[item.name] = true;
    });
    // Grupo "Financeiro Interno" fica só na lista de ferramentas (is_staff),
    // montada depois deste estado — checagem própria e simples por pathname
    // em vez de depender de visibleToolsItems ainda não calculado aqui.
    if (location.pathname === '/interno/financeiro') {
      initial['Financeiro Interno'] = true;
    }
    return initial;
  });

  const toggleGroup = (name: string) => {
    setExpandedGroups((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  // Conta is_staff tem profile.school = "Eleve Interno" (ver seed_internal_school,
  // eleveia-api) — Atendimentos, Pedagógico, Agente I.A etc. continuam sem sentido
  // (não há alunos/agente de WhatsApp pra gerir), mas "Setores" (Boards + Triagem)
  // passa a fazer sentido: é onde o time organiza o próprio trabalho interno
  // (suporte às escolas, bugs, etc.) — mesma tela que qualquer escola usa,
  // ignorando rolesRequired/moduleRequired (são regras de config da escola
  // cliente, não se aplicam à navegação interna do time).
  const visibleNavItems = isStaff
    ? mainNavItems.filter((item) => item.name === 'Setores')
    : mainNavItems
        .filter((item) => {
          if (item.disabled) return false;
          if (item.rolesRequired && !item.rolesRequired.includes(userRole)) return false;
          const bypassed = !!item.moduleBypassRoles?.includes(userRole);
          return hasModule(item.moduleRequired) || bypassed;
        });

  const visibleToolsItems: NavItem[] = isStaff
    ? [
        { name: 'Perfil da Escola', icon: <Building2 className="size-5" />, path: '/escola' },
        { name: 'Dashboard administrativo', icon: <Gauge className="size-5" />, path: '/interno/dashboard' },
        { name: 'Revisar cadastros', icon: <ShieldCheck className="size-5" />, path: '/interno/escolas-pendentes' },
        { name: 'Planos', icon: <Wallet className="size-5" />, path: '/interno/planos' },
        {
          name: 'Financeiro Interno',
          icon: <DollarSign className="size-5" />,
          path: '/interno/financeiro',
          children: [
            { name: 'Visão Geral', icon: <LayoutDashboard className="size-4" />, path: '/interno/financeiro?tab=dashboard' },
            { name: 'Contas', icon: <Wallet className="size-4" />, path: '/interno/financeiro?tab=contas' },
            { name: 'Despesas', icon: <Receipt className="size-4" />, path: '/interno/financeiro?tab=despesas' },
            { name: 'Receitas', icon: <TrendingUp className="size-4" />, path: '/interno/financeiro?tab=receitas' },
            { name: 'Folha de Pagamento', icon: <Users className="size-4" />, path: '/interno/financeiro?tab=folha' },
            { name: 'Impostos', icon: <Landmark className="size-4" />, path: '/interno/financeiro?tab=impostos' },
            { name: 'Categorias', icon: <Tag className="size-4" />, path: '/interno/financeiro?tab=categorias' },
          ],
        },
        { name: 'Central de Feedback', icon: <MessageSquare className="size-5" />, path: '/interno/feedback' },
        { name: 'Workspace', icon: <Headphones className="size-5" />, path: '/interno/workspace' },
        { name: 'Boards das escolas', icon: <Trello className="size-5" />, path: '/interno/boards' },
        { name: 'Tutoriais (conteúdo)', icon: <PlayCircle className="size-5" />, path: '/interno/tutoriais' },
        { name: 'Chat da Equipe', icon: <MessageCircle className="size-5" />, path: '/chat' },
        { name: 'Chat das escolas', icon: <MessageCircle className="size-5" />, path: '/interno/chat' },
      ]
    : toolsNavItems.filter((item) => {
        if (item.disabled) return false;
        if (item.rolesRequired && !item.rolesRequired.includes(userRole)) return false;
        return hasModule(item.moduleRequired);
      });

  const getBadge = (item: NavItem): number | undefined => {
    if (item.name === 'Início') {
      return pendingActionsCount > 0 ? pendingActionsCount : undefined;
    }
    if (item.name === 'Solicitações') {
      return solicitationsInboxCount > 0 ? solicitationsInboxCount : undefined;
    }
    if (item.name === 'Atendimentos') {
      return attendanceUnreadCount > 0 ? attendanceUnreadCount : undefined;
    }
    return item.badge;
  };

  return (
    <aside
      data-app-sidebar
      className={`
        bg-white dark:bg-[#0a0b0d] border-r border-slate-200 dark:border-white/[0.08] flex flex-col transition-all duration-300 flex-shrink-0
        ${collapsed ? 'w-[72px]' : 'w-60'}
        md:relative md:translate-x-0
        fixed inset-y-0 left-0 z-50
        ${mobileOpen ? 'translate-x-0 shadow-xl' : '-translate-x-full md:translate-x-0'}
      `}
    >
      {/* Logo */}
      <div className="h-14 flex items-center justify-between px-3 border-b border-slate-100 dark:border-white/[0.06]">
        <div className="flex items-center gap-2.5 overflow-hidden">
          <img src={logoIcone} alt="App Logo" className="size-8 flex-shrink-0 object-contain" />
          {!collapsed && (
            <img src={logoText} alt="App Name" className="h-4 flex-shrink-0 object-contain object-left dark:brightness-0 dark:invert" />
          )}
        </div>
        <button
          onClick={onToggle}
          className="size-7 rounded-md hover:bg-slate-100 dark:hover:bg-white/[0.06] flex items-center justify-center text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300 transition-colors flex-shrink-0"
        >
          {collapsed ? <ChevronRight className="size-4" /> : <ChevronLeft className="size-4" />}
        </button>
      </div>

      {/* School Selector — navega para /escola ao clicar. Conta is_staff não tem escola. */}
      {!collapsed && !isStaff && (
        <div className="px-3 py-2.5 border-b border-slate-100 dark:border-white/[0.06]">
          <button
            onClick={() => navigate('/escola')}
            className="w-full flex items-center justify-between px-2.5 py-2 bg-slate-50 hover:bg-slate-100 dark:bg-white/[0.03] dark:hover:bg-white/[0.06] rounded-lg transition-colors group"
          >
            <div className="flex items-center gap-2 min-w-0">
              <Home className="size-3.5 text-slate-500 dark:text-slate-400 flex-shrink-0" />
              <span className="text-xs font-medium text-slate-700 dark:text-slate-200 truncate">{schoolName}</span>
              <span
                aria-label={
                  waStatus === 'connected'
                    ? 'WhatsApp conectado'
                    : waStatus === 'connecting'
                    ? 'WhatsApp conectando'
                    : 'WhatsApp desconectado'
                }
                title={
                  waStatus === 'connected'
                    ? 'WhatsApp conectado'
                    : waStatus === 'connecting'
                    ? 'WhatsApp conectando'
                    : 'WhatsApp desconectado'
                }
                className={`size-2 rounded-full flex-shrink-0 ${
                  waStatus === 'connected'
                    ? 'bg-emerald-500'
                    : waStatus === 'connecting'
                    ? 'bg-amber-400 animate-pulse'
                    : 'bg-slate-300 dark:bg-slate-600'
                }`}
              />
            </div>
            <ChevronRight className="size-3.5 text-slate-300 group-hover:text-slate-500 dark:text-slate-600 dark:group-hover:text-slate-400 flex-shrink-0" />
          </button>
        </div>
      )}

      {/* Main Navigation */}
      <nav data-testid="sidebar-nav" className="flex-1 overflow-y-auto py-3 px-2">
        <div className="space-y-0.5">
          {visibleNavItems.map((item) => {
            const active = isActive(item);
            const badge = getBadge(item);
            const visibleChildren = (item.children ?? []).filter(
              (child) => !child.rolesRequired || child.rolesRequired.includes(userRole),
            );
            const hasChildren = visibleChildren.length > 0;
            const expanded = !collapsed && hasChildren && !!expandedGroups[item.name];
            return (
              <div key={item.name}>
                <button
                  title={item.name === 'Atendimentos' ? `Atendimentos — ${CONVERSATION_SHORTCUT_LABEL} abre o chat de qualquer tela` : undefined}
                  onClick={() => (hasChildren && !collapsed ? toggleGroup(item.name) : handleNav(item))}
                  className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg transition-all group relative ${
                    active
                      ? 'bg-indigo-50 text-indigo-600 dark:bg-indigo-500/15 dark:text-indigo-300'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-white/[0.06] dark:hover:text-slate-100'
                  }`}
                >
                  <span className={`flex-shrink-0 ${
                    active ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-400 group-hover:text-slate-600 dark:text-slate-500 dark:group-hover:text-slate-300'
                  }`}>
                    {item.icon}
                  </span>
                  {!collapsed && (
                    <>
                      <span className="text-sm font-medium flex-1 text-left">{item.name}</span>
                      {badge !== undefined && (
                        <Badge
                          className={`${
                            item.badgeClassName ?? 'bg-indigo-600'
                          } text-white text-[10px] px-1.5 py-0 border-none h-5 min-w-[20px] flex items-center justify-center`}
                        >
                          {badge}
                        </Badge>
                      )}
                      {hasChildren && (
                        <ChevronDown
                          className={`size-3.5 flex-shrink-0 text-slate-300 dark:text-slate-600 transition-transform ${expanded ? 'rotate-180' : ''}`}
                        />
                      )}
                    </>
                  )}
                  {active && !hasChildren && (
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-6 bg-indigo-600 dark:bg-indigo-400 rounded-r-full" />
                  )}
                </button>

                {expanded && (
                  <div className="mt-0.5 ml-4 pl-3 border-l border-slate-100 dark:border-white/[0.06] space-y-0.5">
                    {visibleChildren.map((child) => {
                      const childActive = isChildActive(child);
                      const childBadge = getBadge(child);
                      return (
                        <button
                          key={child.name}
                          onClick={() => handleNav(child)}
                          className={`w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg transition-all group relative ${
                            childActive
                              ? 'bg-indigo-50 text-indigo-600 dark:bg-indigo-500/15 dark:text-indigo-300'
                              : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-white/[0.06] dark:hover:text-slate-100'
                          }`}
                        >
                          <span className={`flex-shrink-0 ${
                            childActive ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-400 group-hover:text-slate-600 dark:text-slate-500 dark:group-hover:text-slate-300'
                          }`}>
                            {child.icon}
                          </span>
                          <span className="text-[13px] font-medium flex-1 text-left">{child.name}</span>
                          {childBadge !== undefined && (
                            <Badge
                              className={`${
                                child.badgeClassName ?? 'bg-red-500'
                              } text-white text-[10px] px-1.5 py-0 border-none h-4 min-w-[18px] flex items-center justify-center`}
                            >
                              {childBadge}
                            </Badge>
                          )}
                          {childActive && (
                            <div className="absolute left-[-13px] top-1/2 -translate-y-1/2 w-[3px] h-5 bg-indigo-600 dark:bg-indigo-400 rounded-r-full" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {visibleToolsItems.length > 0 && (
          <>
            {!collapsed && (
              <div className="mt-5 mb-2 px-2.5">
                <p className="text-[10px] font-semibold text-slate-300 dark:text-slate-600 uppercase tracking-widest">Ferramentas</p>
              </div>
            )}
            <div className="space-y-0.5 mt-1">
              {visibleToolsItems.map((item) => {
                const active = isActive(item);
                const hasChildren = !!item.children?.length;
                const expanded = !collapsed && hasChildren && !!expandedGroups[item.name];
                return (
                  <div key={item.name}>
                    <button
                      onClick={() => (hasChildren && !collapsed ? toggleGroup(item.name) : handleNav(item))}
                      className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg transition-all group relative ${
                        active
                          ? 'bg-indigo-50 text-indigo-600 dark:bg-indigo-500/15 dark:text-indigo-300'
                          : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-white/[0.06] dark:hover:text-slate-100'
                      }`}
                    >
                      <span className={`flex-shrink-0 ${
                        active ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-400 group-hover:text-slate-600 dark:text-slate-500 dark:group-hover:text-slate-300'
                      }`}>
                        {item.icon}
                      </span>
                      {!collapsed && (
                        <>
                          <span className="text-sm font-medium flex-1 text-left">{item.name}</span>
                          {hasChildren && (
                            <ChevronDown
                              className={`size-3.5 flex-shrink-0 text-slate-300 dark:text-slate-600 transition-transform ${expanded ? 'rotate-180' : ''}`}
                            />
                          )}
                        </>
                      )}
                      {active && !hasChildren && (
                        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-6 bg-indigo-600 dark:bg-indigo-400 rounded-r-full" />
                      )}
                    </button>

                    {expanded && (
                      <div className="mt-0.5 ml-4 pl-3 border-l border-slate-100 dark:border-white/[0.06] space-y-0.5">
                        {item.children!.map((child) => {
                          const childActive = isChildActive(child);
                          return (
                            <button
                              key={child.name}
                              onClick={() => handleNav(child)}
                              className={`w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg transition-all group relative ${
                                childActive
                                  ? 'bg-indigo-50 text-indigo-600 dark:bg-indigo-500/15 dark:text-indigo-300'
                                  : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-white/[0.06] dark:hover:text-slate-100'
                              }`}
                            >
                              <span className={`flex-shrink-0 ${
                                childActive ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-400 group-hover:text-slate-600 dark:text-slate-500 dark:group-hover:text-slate-300'
                              }`}>
                                {child.icon}
                              </span>
                              <span className="text-[13px] font-medium flex-1 text-left">{child.name}</span>
                              {childActive && (
                                <div className="absolute left-[-13px] top-1/2 -translate-y-1/2 w-[3px] h-5 bg-indigo-600 dark:bg-indigo-400 rounded-r-full" />
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </nav>

      {/* Health Status Widget — checa WhatsApp/SIGA/Agente da escola; sem sentido pra is_staff */}
      {!isStaff && <HealthStatusWidget collapsed={collapsed} />}

      {/* Tema claro/escuro */}
      {DARK_THEME_ENABLED && (
        <div className="px-2.5 py-1.5 border-t border-slate-100 dark:border-white/[0.06]">
          <ThemeToggle collapsed={collapsed} />
        </div>
      )}

      {/* User Profile */}
      <div className="p-2.5 border-t border-slate-100 dark:border-white/[0.06]">
        <button
          onClick={() => navigate('/profile')}
          className={`w-full flex items-center gap-2.5 px-2 py-2 rounded-lg hover:bg-slate-50 dark:hover:bg-white/[0.06] transition-colors group ${
            collapsed ? 'justify-center' : ''
          }`}
        >
          <UserAvatar
            name={fullName}
            avatarUrl={user?.profile?.avatar_url}
            className="size-8 ring-2 ring-slate-100 dark:ring-white/[0.08] flex-shrink-0"
            fallbackClassName="bg-gradient-to-br from-indigo-600 to-violet-600 text-white text-xs font-semibold"
          />
          {!collapsed && (
            <>
              <div className="flex-1 min-w-0 text-left">
                <p className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate leading-tight">{fullName}</p>
                <p className="text-[11px] text-slate-400 dark:text-slate-500 truncate leading-tight">{roleDisplay}</p>
              </div>
              <MoreHorizontal className="size-4 text-slate-300 group-hover:text-slate-500 dark:text-slate-600 dark:group-hover:text-slate-400 flex-shrink-0" />
            </>
          )}
        </button>
      </div>
    </aside>
  );
}
